import { Injectable, OnDestroy, inject, NgZone, Injector } from '@angular/core';
import { Subject, Observable, BehaviorSubject } from 'rxjs';

// Add ThemeService to the core imports to allow dynamic toggling
import {
  Univer,
  IWorkbookData,
  ICommandService,
  ICommandInfo,
  LocaleType,
  UniverInstanceType,
  ThemeService,
  FUniver,
} from '@univerjs/core';
import { IUniverInstanceService } from '@univerjs/core';

// CRITICAL FIX: Removed the volatile Facade API and imported native Render engine components instead
import { IRenderManagerService, SheetExtension } from '@univerjs/engine-render';

import { UniverDocsPlugin } from '@univerjs/docs';
import { UniverDocsUIPlugin } from '@univerjs/docs-ui';
import { UniverFormulaEnginePlugin } from '@univerjs/engine-formula';
import { UniverRenderEnginePlugin } from '@univerjs/engine-render';
import { UniverSheetsPlugin, SetRangeValuesCommand } from '@univerjs/sheets';
import { UniverSheetsFormulaPlugin } from '@univerjs/sheets-formula';
import { UniverSheetsNumfmtPlugin } from '@univerjs/sheets-numfmt';
import { UniverSheetsUIPlugin } from '@univerjs/sheets-ui';
import { UniverUIPlugin } from '@univerjs/ui';

import { UniverSheetsFormulaUIPlugin } from '@univerjs/sheets-formula-ui';
import { UniverSheetsNumfmtUIPlugin } from '@univerjs/sheets-numfmt-ui';

// Native Data Validation Plugins
import { UniverDataValidationPlugin } from '@univerjs/data-validation';
import { UniverSheetsDataValidationPlugin } from '@univerjs/sheets-data-validation';
import { UniverSheetsDataValidationUIPlugin } from '@univerjs/sheets-data-validation-ui';

import moment from 'moment-timezone';

// Import the English locales from the UI plugins
import DesignEnUS from '@univerjs/design/locale/en-US';
import { defaultTheme } from '@univerjs/design';
import UIEnUS from '@univerjs/ui/locale/en-US';
import DocsUIEnUS from '@univerjs/docs-ui/locale/en-US';
import SheetsUIEnUS from '@univerjs/sheets-ui/locale/en-US';

// CRITICAL FIX: Add the missing English locales for the new plugins
import SheetsFormulaUIEnUS from '@univerjs/sheets-formula-ui/locale/en-US';
import SheetsNumfmtUIEnUS from '@univerjs/sheets-numfmt-ui/locale/en-US';

import { EntityFieldDef, CellUpdateEvent } from '../models/spreadsheet.model';
import {
  mapEntityToUniverWorkbook,
  validateCellValue,
  parseAndConvertCellValue,
  parseTabularPasteData,
} from '../utils/spreadsheet.utils';
import { SPREADSHEET_CONFIG } from '../config/spreadsheet.config';

// Import Notification Service and Selection Manager for advanced UX
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SheetsSelectionsService } from '@univerjs/sheets';

// CRITICAL FIX: Global agnostic array to hold field titles for the canvas extension
export let CUSTOM_COLUMN_HEADERS: string[] = [];

/**
 * @class ColumnHeaderCustomExtension
 * @description A custom render extension to manually draw project-agnostic field titles on the column headers,
 * completely replacing the default A, B, C letters using the native Canvas API.
 */
export class ColumnHeaderCustomExtension extends SheetExtension {
  override uKey = 'ColumnHeaderCustomExtension';
  private hasLoggedDraw = false; // Prevent console spam on every frame redraw

  override get zIndex() {
    return 50; // Render securely over the default text layer
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  override draw(ctx: any, _parentScale: any, spreadsheetSkeleton: any) {
    if (!this.hasLoggedDraw) {
      console.log(
        '[Univer Debug] ColumnHeaderCustomExtension draw() invoked successfully!',
      );
      this.hasLoggedDraw = true;
    }

    if (!spreadsheetSkeleton) return;

    const {
      rowColumnSegment,
      columnHeaderHeight = 20,
      columnWidthAccumulation,
    } = spreadsheetSkeleton;
    if (!rowColumnSegment || !columnWidthAccumulation) return;

    const { startColumn, endColumn } = rowColumnSegment;

    ctx.save();
    // Offset slightly for crisp Canvas text rendering
    ctx.translate(0.5, 0.5);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 13px Arial'; // Match Univer's default header font size

    let preColumnPosition = 0;
    const columnWidthAccumulationLength = columnWidthAccumulation.length;

    for (let c = startColumn - 1; c <= endColumn; c++) {
      if (c < 0 || c > columnWidthAccumulationLength - 1) continue;

      const columnEndPosition = columnWidthAccumulation[c];
      if (preColumnPosition === columnEndPosition) continue;

      const colWidth = columnEndPosition - preColumnPosition;

      // 1. Draw solid background to perfectly cover default A, B, C
      ctx.fillStyle = '#F8F9FA';
      // Expand the rectangle slightly to prevent native border bleed-through
      ctx.fillRect(
        preColumnPosition - 1,
        -1,
        colWidth + 2,
        columnHeaderHeight + 2,
      );

      // 2. Redraw the crisp separator border
      ctx.strokeStyle = '#D9D9D9';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(columnEndPosition, 0);
      ctx.lineTo(columnEndPosition, columnHeaderHeight);
      ctx.stroke();

      // 3. Draw your exact Field Title dynamically
      ctx.fillStyle = '#333333';
      const title = CUSTOM_COLUMN_HEADERS[c] || '';
      const middleCellPos = preColumnPosition + colWidth / 2;
      ctx.fillText(title, middleCellPos, columnHeaderHeight / 2);

      preColumnPosition = columnEndPosition;
    }

    ctx.restore();
  }
}

/**
 * @class UniverSpreadsheetService
 * @description Manages the lifecycle, plugins, and event interception for the Univer instance.
 */
@Injectable({
  providedIn: 'root',
})
export class UniverSpreadsheetService implements OnDestroy {
  private univer: Univer | null = null;

  // CRITICAL FIX: Lazily inject the notification service to prevent NG0200 Circular Dependency errors
  // caused by NG-ZORRO's internal Icon Service dependencies firing too early during app bootstrap.
  private injector = inject(Injector);
  private get notificationService(): NzNotificationService {
    return this.injector.get(NzNotificationService);
  }

  // Prevents notification spam if multiple cells trigger errors simultaneously
  private lastNotificationTime = new Map<string, number>();

  // Observables for parent components to react to UI events natively
  private cellUpdateSubject = new Subject<CellUpdateEvent>();
  public cellUpdates$ = this.cellUpdateSubject.asObservable();

  private selectionSubject = new BehaviorSubject<any | null>(null);
  public selectionUpdates$ = this.selectionSubject.asObservable();

  private deletedRowsSubject = new Subject<any[]>();
  public deletedRows$ = this.deletedRowsSubject.asObservable();

  // Keep a reference to current selections to intercept deletions
  private currentSelections: any[] = [];

  private ngZone = inject(NgZone);

  private currentFields: EntityFieldDef[] = [];
  private currentDataMap: any[] = [];
  private currentModelName: string = 'default_model';

  // Timeout reference for debouncing configuration saves
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private configSaveTimeout: any = null;

  /**
   * Initializes Univer into a specific DOM container with required plugins.
   * @param {HTMLElement} container - The DOM element to host the canvas.
   * @param {boolean} isDarkMode - Whether to initialize the engine in dark mode.
   */
  public init(container: HTMLElement, isDarkMode: boolean = false): void {
    this.destroy(); // Ensure clean slate

    // Run outside Angular so Zone.js doesn't swallow keyboard/canvas events
    this.ngZone.runOutsideAngular(() => {
      this.univer = new Univer({
        theme: defaultTheme,
        locale: LocaleType.EN_US,
        locales: {
          [LocaleType.EN_US]: Object.assign(
            {},
            DesignEnUS,
            UIEnUS,
            DocsUIEnUS,
            SheetsUIEnUS,
            SheetsFormulaUIEnUS,
            SheetsNumfmtUIEnUS,
          ),
        },
      });

      // Core Engines
      this.univer.registerPlugin(UniverRenderEnginePlugin);
      this.univer.registerPlugin(UniverFormulaEnginePlugin);

      // UI Plugins
      this.univer.registerPlugin(UniverUIPlugin, { container });
      this.univer.registerPlugin(UniverDocsPlugin, { hasScroll: false });
      this.univer.registerPlugin(UniverDocsUIPlugin);

      // Sheets Plugins
      this.univer.registerPlugin(UniverSheetsPlugin);
      this.univer.registerPlugin(UniverSheetsUIPlugin);
      this.univer.registerPlugin(UniverSheetsNumfmtPlugin);
      this.univer.registerPlugin(UniverSheetsNumfmtUIPlugin);
      this.univer.registerPlugin(UniverSheetsFormulaPlugin);
      this.univer.registerPlugin(UniverSheetsFormulaUIPlugin);

      // Data Validation Plugins
      this.univer.registerPlugin(UniverDataValidationPlugin);
      this.univer.registerPlugin(UniverSheetsDataValidationPlugin);
      this.univer.registerPlugin(UniverSheetsDataValidationUIPlugin, {
        showEditOnDropdown: true,
      });

      this.setupInterceptors();

      // Attach paste interceptor to process Excel/Google Sheets tabular data
      container.addEventListener('paste', this.handlePaste.bind(this));
    });
  }
  /**
   * Loads data into the Univer workbook, replacing the existing sheet.
   * Dynamically restores custom configurations (widths, zoom, colors, etc.) if available.
   * @param {any[]} data - Array of backend records.
   * @param {EntityFieldDef[]} fields - Column definitions.
   * @param {string} projectModelName - The unique entity name for config scoping.
   */
  public loadData(
    data: any[],
    fields: EntityFieldDef[],
    projectModelName: string,
  ): void {
    if (!this.univer) return;

    // CRITICAL FIX: Safely dispose of the existing workbook before injecting new data.
    // Wrapped in a try-catch to swallow known Univer UI controller crashes (e.g., SheetNumfmtUIController).
    try {
      const univerAPI = FUniver.newAPI(this.univer as any) as any;
      const existingWorkbook = univerAPI.getActiveWorkbook();
      if (existingWorkbook) {
        existingWorkbook.dispose();
      }
    } catch (e) {
      console.warn(
        '[Univer Debug] Suppressed internal library error while disposing old workbook:',
        e,
      );
    }

    this.currentFields = fields;
    this.currentDataMap = data; // Keep reference to look up original IDs

    // CRITICAL FIX: Lock the current model name tightly for this specific sheet session
    this.currentModelName = projectModelName;

    // CRITICAL FIX: Populate the global headers array for the custom canvas extension
    CUSTOM_COLUMN_HEADERS = fields.map((f) => f.title || f.key);

    let workbookData = mapEntityToUniverWorkbook(data, fields);

    // If no data was passed, initialize an empty object.
    const sheetKeysNew = Object.keys(workbookData.sheets || {});
    if (sheetKeysNew.length > 0) {
      const firstSheetId = sheetKeysNew[0];
      const sheet = workbookData.sheets[firstSheetId];
      if (!sheet.cellData || Object.keys(sheet.cellData).length === 0) {
        // Some Univer extensions crash if cellData is fully omitted, so we pass an empty shell with at least one row
        sheet.cellData = { 0: {} };
      }
    }

    // Attempt to load the user's localized configuration from storage
    const configKey = `${this.currentModelName}_sheetConfig`;
    let storedConfigStr = null;
    if (typeof window !== 'undefined') {
      storedConfigStr = localStorage.getItem(configKey);
    }

    let hasConfig = false;

    if (storedConfigStr) {
      try {
        const storedConfig = JSON.parse(storedConfigStr);
        const newSheetId = Object.keys(workbookData.sheets)[0];
        const newSheet = workbookData.sheets[newSheetId];

        const storedSheetId = Object.keys(storedConfig.sheets)[0];
        const storedSheet = storedConfig.sheets[storedSheetId];

        const systemColors = ['#FEF9C3', '#FECACA', '#DCFCE7', '#FDE047'];

        // CRITICAL FIX: Sanitize the global styles dictionary from old buggy saved configs
        if (storedConfig.styles) {
          for (const styleId in storedConfig.styles) {
            const s = storedConfig.styles[styleId];
            if (s && s.bg && s.bg.rgb) {
              if (systemColors.includes(s.bg.rgb.toUpperCase())) {
                delete s.bg;
              }
            }
          }
        }

        if (storedSheet && newSheet) {
          if (!storedSheet.cellData) storedSheet.cellData = {};

          // Clear legacy layout values to prevent ghost data from lingering
          for (const r in storedSheet.cellData) {
            for (const c in storedSheet.cellData[r]) {
              if (storedSheet.cellData[r][c]) {
                delete storedSheet.cellData[r][c].v;
                delete storedSheet.cellData[r][c].t;
                delete storedSheet.cellData[r][c].p;

                // Sanitize any previously saved inline system colors from older buggy configs
                const s = storedSheet.cellData[r][c].s;
                if (s && typeof s === 'object' && s.bg && s.bg.rgb) {
                  if (systemColors.includes(s.bg.rgb.toUpperCase())) {
                    delete s.bg;
                    if (Object.keys(s).length === 0)
                      delete storedSheet.cellData[r][c].s;
                  }
                }
              }
            }
          }

          // Inject the clean data values natively into the recovered configuration shell
          for (const r in newSheet.cellData) {
            if (!storedSheet.cellData[r as any])
              storedSheet.cellData[r as any] = {};
            for (const c in newSheet.cellData[r as any]) {
              if (!storedSheet.cellData[r as any][c as any])
                storedSheet.cellData[r as any][c as any] = {};
              storedSheet.cellData[r as any][c as any].v =
                newSheet.cellData[r as any][c as any].v;
              storedSheet.cellData[r as any][c as any].t =
                newSheet.cellData[r as any][c as any].t;
              // Leaves user's custom cell styles (cell.s) fully intact
            }
          }

          storedSheet.rowCount = Math.max(
            storedSheet.rowCount || 0,
            newSheet.rowCount || 0,
          );
          storedSheet.columnCount = Math.max(
            storedSheet.columnCount || 0,
            newSheet.columnCount || 0,
          );
        }

        workbookData = storedConfig;
        hasConfig = true;
      } catch (e) {
        console.warn('[Univer Debug] Failed to apply stored sheet config', e);
      }
    }

    // Apply default visibility rules only if no user-config exists
    if (!hasConfig) {
      const sheetKeys = Object.keys(workbookData.sheets || {});
      if (sheetKeys.length > 0) {
        const sheet = workbookData.sheets[sheetKeys[0]];
        if (!sheet.columnData) {
          sheet.columnData = {};
        }

        fields.forEach((field: any, colIndex: number) => {
          const colData = sheet.columnData as any;
          if (!colData[colIndex]) {
            colData[colIndex] = {};
          }
          colData[colIndex].hd = field.hiddenByDefault ? 1 : 0; // 1 = hidden, 0 = visible
        });
      }
    }

    // CRITICAL FIX: Create sheet outside Angular to ensure UI listeners bind correctly
    this.ngZone.runOutsideAngular(() => {
      const workbook = this.univer!.createUnit(
        UniverInstanceType.UNIVER_SHEET,
        workbookData,
      );

      // Apply Native Features
      try {
        const univerAPI = FUniver.newAPI(this.univer as any) as any;
        const fWorkbook = univerAPI.getActiveWorkbook();
        const fWorksheet = fWorkbook?.getActiveSheet();

        if (fWorksheet) {
          // Apply intelligent zoom
          if (!hasConfig) {
            fWorksheet.zoom(1.1);
            // Optionally log verification: const zoomRatio = fWorksheet.getZoom();
          }

          fields.forEach((field, colIndex) => {
            // 1. Map Defined Names strictly for formula access globally
            try {
              const colLetter = this.getColumnLetter(colIndex);
              const sheetName = fWorksheet.getName();
              const rangeStr = `${sheetName}!$${colLetter}:$${colLetter}`;

              // Ensure name is clean (alphanumeric, no spaces, cannot start with number)
              const safeName = field.key
                .replace(/[^a-zA-Z0-9_]/g, '_')
                .replace(/^([0-9])/, '_$1');
              fWorkbook.insertDefinedName(safeName, rangeStr);
            } catch (e) {
              console.warn(
                '[Univer Debug] Failed to insert defined name for',
                field.key,
                e,
              );
            }

            // 2. Map Data Validation Rules
            let ruleBuilder = univerAPI.newDataValidation();
            let hasRule = false;

            if (
              field.type === 'enum' &&
              field.enumValues &&
              field.enumValues.length > 0
            ) {
              ruleBuilder.requireValueInList(field.enumValues);
              hasRule = true;
            } else if (field.type === 'boolean') {
              ruleBuilder.requireCheckbox();
              hasRule = true;
            } else if (field.type === 'date') {
              ruleBuilder.requireDateOnOrAfter(new Date('1900-01-01'));
              hasRule = true;
            }

            if (hasRule) {
              const rule = ruleBuilder
                .setOptions({
                  allowBlank: !field.required,
                  showErrorMessage: true,
                  errorStyle: univerAPI.Enum.DataValidationErrorStyle.WARNING,
                  error: `Validation failed for ${field.title}`,
                })
                .build();

              const fRange = fWorksheet.getRange(1, colIndex, 1000, 1);
              fRange.setDataValidation(rule);
            }
          });
        }
      } catch (e) {
        console.warn('[Univer Debug] Failed to apply Native Features.', e);
      }

      // If the spreadsheet is loading entirely fresh, run the autosize operation
      if (!hasConfig) {
        setTimeout(() => this.autoSizeColumns(), 200);
      }

      // CRITICAL FIX: Increase timeout slightly to guarantee Canvas engine is fully mounted
      setTimeout(() => {
        try {
          const renderManager = this.univer!.__getInjector().get(
            IRenderManagerService,
          );
          const currentRender = renderManager.getRenderById(
            workbook.getUnitId(),
          );

          if (currentRender) {
            const availableKeys = Array.from(currentRender.components.keys());
            const targetKey = availableKeys.find((k) =>
              (k as string).toLowerCase().includes('columnheader'),
            );

            if (targetKey) {
              const columnHeader = currentRender.components.get(
                targetKey,
              ) as any;
              if (
                columnHeader &&
                !columnHeader.getExtensionByKey('ColumnHeaderCustomExtension')
              ) {
                columnHeader.register(new ColumnHeaderCustomExtension());

                if (currentRender.mainComponent) {
                  currentRender.mainComponent.makeDirty();
                }
              }
            }
          }
        } catch (e) {
          console.error(
            '[Univer Debug] Crash while attaching Custom Header Extension',
            e,
          );
        }
      }, 300); // Wait 300ms
    });
  }

  /**
   * Helper function to convert numeric 0-based column indices to Excel letters (A, B... Z, AA).
   */
  private getColumnLetter(colIndex: number): string {
    let letter = '';
    let temp = colIndex;
    while (temp >= 0) {
      letter = String.fromCharCode((temp % 26) + 65) + letter;
      temp = Math.floor(temp / 26) - 1;
    }
    return letter;
  }

  /**
   * Sets up command interceptors to listen for cell edits and layout structural changes.
   */
  private setupInterceptors(): void {
    if (!this.univer) return;

    const commandService = this.univer.__getInjector().get(ICommandService);

    // CRITICAL FIX: Intercept and block structural changes BEFORE they are executed.
    commandService.beforeCommandExecuted((commandInfo: ICommandInfo) => {
      const id = commandInfo.id.toLowerCase();

      const isRowDeletion =
        id.includes('remove-row') || id.includes('delete-row');
      const isColDeletion =
        id.includes('remove-col') || id.includes('delete-col');

      const isStructuralCommand =
        id.includes('insert-row') ||
        id.includes('insert-col') ||
        id.includes('add-row') ||
        id.includes('add-col') ||
        isRowDeletion ||
        isColDeletion;

      if (isStructuralCommand) {
        if (isRowDeletion) {
          // Emit the deleted rows to the parent so it can prompt the user
          const deletedRecords: any[] = [];

          const instanceService = this.univer!.__getInjector().get(
            IUniverInstanceService,
          );
          const workbook = instanceService.getCurrentUnitForType<any>(
            UniverInstanceType.UNIVER_SHEET,
          );
          const worksheet = workbook.getActiveSheet();
          const subUnitId = worksheet.getSheetId();
          const unitId = workbook.getUnitId();

          const silentMatrix: any = {};
          let minRow = Infinity,
            maxRow = -Infinity;

          this.currentSelections.forEach((sel) => {
            const range = sel.range;
            if (range) {
              const redStyle = { bg: { rgb: '#FECACA' } };
              for (let r = range.startRow; r <= range.endRow; r++) {
                if (this.currentDataMap[r]) {
                  deletedRecords.push({
                    ...this.currentDataMap[r],
                    _rowIndex: r,
                  });
                }

                // Mark visually as deleted
                silentMatrix[r] = {};
                for (let c = 0; c < this.currentFields.length; c++) {
                  silentMatrix[r][c] = { s: redStyle };
                }
                minRow = Math.min(minRow, r);
                maxRow = Math.max(maxRow, r);
              }
            }
          });

          if (deletedRecords.length > 0) {
            if (maxRow >= 0 && minRow !== Infinity) {
              this.executeCommandSilently(SetRangeValuesCommand.id, {
                subUnitId,
                unitId,
                range: {
                  startRow: minRow,
                  endRow: maxRow,
                  startColumn: 0,
                  endColumn: this.currentFields.length - 1,
                },
                value: silentMatrix,
              });
            }

            this.ngZone.run(() => {
              this.deletedRowsSubject.next(deletedRecords);
            });
          } else {
            this.ngZone.run(() => {
              this.notificationService.warning(
                SPREADSHEET_CONFIG.texts.actionBlocked,
                $localize`:@@spreadsheet.rowDeletionRequiresSelection:Row deletion requires selecting existing records first.`,
              );
            });
          }
        } else {
          // Standard structural block for columns or insertions
          this.ngZone.run(() => {
            this.notificationService.warning(
              SPREADSHEET_CONFIG.texts.actionBlocked,
              SPREADSHEET_CONFIG.texts.actionBlockedDesc,
            );
          });
        }
        throw new Error('CanceledError');
      }
    });

    // Commands that change the UI state/configuration of the sheet
    const configCommandIds = [
      'sheet.command.set-col-width',
      'sheet.command.set-col-hidden',
      'sheet.mutation.set-col-visible',
      'sheet.mutation.set-col-hidden',
      'sheet.command.set-zoom-ratio',
      'sheet.command.set-range-values', // Catch styling/format changes
      'sheet.command.move-cols',
    ];

    // Listen for executions natively
    commandService.onCommandExecuted((commandInfo: ICommandInfo) => {
      const params = commandInfo.params as any;

      if (params && params._isInternal === true) {
        return;
      }

      // Automatically persist configurations to local storage when layout is altered
      if (
        configCommandIds.includes(commandInfo.id) ||
        commandInfo.id === SetRangeValuesCommand.id
      ) {
        this.scheduleConfigSave();
      }

      if (commandInfo.id === 'sheet.operation.set-selections') {
        const selections = params?.selections;
        if (selections && selections.length > 0) {
          this.currentSelections = selections;
          const startRow = selections[0].range.startRow;
          this.ngZone.run(() => {
            const record = this.currentDataMap[startRow];
            this.selectionSubject.next(record || null);
          });
        }
      }

      if (commandInfo.id === 'sheet.command.move-range') {
        this.ngZone.run(() => {
          this.handleMoveRange(params);
        });
        return;
      }

      if (
        commandInfo.id === SetRangeValuesCommand.id ||
        commandInfo.id === 'sheet.command.set-range-values' ||
        commandInfo.id === 'sheet.mutation.set-range-values'
      ) {
        this.ngZone.run(() => {
          this.handleCellEdit(params);
        });
      }
    });
  }

  /**
   * Schedules a debounced snapshot save of the current spreadsheet layout.
   */
  private scheduleConfigSave(): void {
    if (!this.currentModelName) return;
    clearTimeout(this.configSaveTimeout);
    this.configSaveTimeout = setTimeout(() => {
      this.saveCurrentConfig();
    }, 2000);
  }

  /**
   * Extracts the current Universal configuration state and saves it to local storage.
   * Strips all data values, formulas, text payloads, and system highlight colors to securely preserve only layout and styles.
   */
  private saveCurrentConfig(): void {
    if (!this.univer || !this.currentModelName || typeof window === 'undefined')
      return;

    try {
      const instanceService = this.univer
        .__getInjector()
        .get(IUniverInstanceService);
      const workbook = instanceService.getCurrentUnitForType<any>(
        UniverInstanceType.UNIVER_SHEET,
      );
      if (!workbook) return;

      const snapshot = workbook.save();
      const systemColors = ['#FEF9C3', '#FECACA', '#DCFCE7', '#FDE047'];

      // CRITICAL FIX: Strip transient system highlight colors from the global styles dictionary so they don't persist
      if (snapshot.styles) {
        for (const styleId in snapshot.styles) {
          const s = snapshot.styles[styleId];
          if (s && s.bg && s.bg.rgb) {
            if (systemColors.includes(s.bg.rgb.toUpperCase())) {
              delete s.bg;
            }
          }
        }
      }

      for (const sheetId in snapshot.sheets) {
        const sheet = snapshot.sheets[sheetId];
        if (sheet.cellData) {
          for (const r in sheet.cellData) {
            for (const c in sheet.cellData[r]) {
              const cell = sheet.cellData[r][c];
              if (cell) {
                // Delete data elements, keep structural styles
                delete cell.v;
                delete cell.p;
                delete cell.f;
                delete cell.t;

                // CRITICAL FIX: Strip transient inline system highlight colors
                if (
                  cell.s &&
                  typeof cell.s === 'object' &&
                  cell.s.bg &&
                  cell.s.bg.rgb
                ) {
                  if (systemColors.includes(cell.s.bg.rgb.toUpperCase())) {
                    delete cell.s.bg;
                  }
                  // Clean up empty style objects
                  if (Object.keys(cell.s).length === 0) {
                    delete cell.s;
                  }
                }

                if (Object.keys(cell).length === 0) {
                  delete sheet.cellData[r][c];
                }
              }
            }
            if (Object.keys(sheet.cellData[r]).length === 0) {
              delete sheet.cellData[r];
            }
          }
        }
      }

      const configKey = `${this.currentModelName}_sheetConfig`;
      localStorage.setItem(configKey, JSON.stringify(snapshot));
    } catch (e) {
      console.warn(
        '[Univer Debug] Failed to auto-save spreadsheet configuration',
        e,
      );
    }
  }

  /**
   * Sets values for multiple cells simultaneously.
   * @param matrix - The data matrix
   * @param range - The bounding range
   */
  public setCellValues(
    matrix: any,
    range: {
      startRow: number;
      endRow: number;
      startColumn: number;
      endColumn: number;
    },
  ): void {
    if (!this.univer) return;
    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;
    const worksheet = workbook.getActiveSheet();
    if (!worksheet) return;

    this.executeCommandSilently(SetRangeValuesCommand.id, {
      subUnitId: worksheet.getSheetId(),
      unitId: workbook.getUnitId(),
      range: range,
      value: matrix,
    });
  }

  /**
   * Handles explicitly the Move Range command (Drag & Drop or Cut & Paste).
   * Generates a synthetic mutation matrix for both the source (cleared) and target (populated) cells,
   * routing it directly into the standard `handleCellEdit` pipeline.
   * @param {any} params - The MoveRangeCommand parameters containing fromRange and toRange.
   */
  private handleMoveRange(params: any): void {
    const fromRange = params.fromRange;
    const toRange = params.toRange;
    if (!fromRange || !toRange) return;

    const instanceService = this.univer!.__getInjector().get(
      IUniverInstanceService,
    );
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;

    const worksheet = workbook.getActiveSheet();
    const cellMatrix = worksheet.getCellMatrix();

    const fakePayload: any = {};

    // 1. Process fromRange (cells were cleared)
    for (let r = fromRange.startRow; r <= fromRange.endRow; r++) {
      if (!fakePayload[r]) fakePayload[r] = {};
      for (let c = fromRange.startColumn; c <= fromRange.endColumn; c++) {
        fakePayload[r][c] = { v: '' }; // Emulate empty cell
      }
    }

    // 2. Process toRange (cells received new values)
    for (let r = toRange.startRow; r <= toRange.endRow; r++) {
      if (!fakePayload[r]) fakePayload[r] = {};
      for (let c = toRange.startColumn; c <= toRange.endColumn; c++) {
        // Read the actual moved value from the sheet's internal memory matrix
        const cell = cellMatrix.getValue(r, c);
        fakePayload[r][c] = { v: cell?.v !== undefined ? cell.v : '' };
      }
    }

    // 3. Route through the standard validation and emission pipeline
    this.handleCellEdit({ value: fakePayload });
  }

  /**
   * Helper function to execute commands without triggering our own interceptors.
   * @param {string} commandId The ID of the command to execute.
   * @param {any} params The parameters for the command.
   * @returns {boolean} True if the command was successfully dispatched, false otherwise.
   */
  private executeCommandSilently(commandId: string, params: any): boolean {
    if (!this.univer) return false;
    const commandService = this.univer.__getInjector().get(ICommandService);

    // 1. Short-circuit if we can natively detect the command is missing
    if (
      (commandService as any).hasCommand &&
      !(commandService as any).hasCommand(commandId)
    ) {
      return false;
    }

    // 2. Suppress console.error temporarily to keep the browser console clean from internal spam
    const originalConsoleError = console.error;
    console.error = () => {};

    let result: any = false;
    try {
      result = commandService.executeCommand(commandId, {
        ...params,
        _isInternal: true,
      });
    } catch (e) {
      result = false;
    } finally {
      console.error = originalConsoleError;
    }

    // 3. CRITICAL FIX: Catch unhandled promise rejections that bypass standard try-catch blocks
    if (result && typeof result.catch === 'function') {
      result.catch(() => {
        /* absorb async rejection */
      });
      return true; // We assume the command is valid since it successfully returned a Promise
    }

    return !!result;
  }

  /**
   * Validates a cell edit event. Emits success or triggers UI feedback for failures.
   * Processes ranges to support single-cell edits, drag-fill, and bulk copy-paste.
   * @param {any} params - The SetRangeValues parameters from Univer.
   */
  private handleCellEdit(params: any): void {
    console.log('--- [Univer Interceptor] Captured Edit Event ---', params);

    let targetRange = params?.range || params?.ranges;
    if (Array.isArray(targetRange)) {
      targetRange = targetRange[0];
    }

    // CRITICAL FIX: Mutations (like Paste) store their data in 'cellValue', while Commands use 'value'.
    const payloadValue = params?.value || params?.cellValue;
    if (!payloadValue) return;

    const instanceService = this.univer!.__getInjector().get(
      IUniverInstanceService,
    );
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;
    const worksheet = workbook.getActiveSheet();

    let silentMatrix: any = null;
    let hasSilentUpdates = false;
    let minRow = Infinity,
      maxRow = -Infinity,
      minCol = Infinity,
      maxCol = -Infinity;

    // Univer sends payloadValue as a Matrix ({ row: { col: ICellData } }) for bulk updates,
    // BUT it sends it as a direct ICellData object ({ v: '...', s: '...' }) for single cell edits!
    const isSingleCellData =
      payloadValue.v !== undefined ||
      payloadValue.s !== undefined ||
      payloadValue.p !== undefined ||
      payloadValue.f !== undefined;

    // Standardize the modified cells into a flat list so we aren't restricted by a broken targetRange
    const cellsToProcess: { row: number; col: number; cellParam: any }[] = [];

    if (isSingleCellData) {
      if (
        targetRange &&
        targetRange.startRow !== undefined &&
        targetRange.startColumn !== undefined
      ) {
        cellsToProcess.push({
          row: targetRange.startRow,
          col: targetRange.startColumn,
          cellParam: payloadValue,
        });
      }
    } else {
      // It's a matrix! Iterate over the actual explicitly modified rows and columns in the payload.
      // This bypasses the issue where 'targetRange' only points to the top-left cell during a paste.
      for (const rowKey of Object.keys(payloadValue)) {
        const rowNum = parseInt(rowKey, 10);
        if (isNaN(rowNum)) continue;

        const colMap = payloadValue[rowKey];
        if (!colMap) continue;

        for (const colKey of Object.keys(colMap)) {
          const colNum = parseInt(colKey, 10);
          if (isNaN(colNum)) continue;

          cellsToProcess.push({
            row: rowNum,
            col: colNum,
            cellParam: colMap[colKey],
          });
        }
      }
    }

    // Process every explicitly modified cell
    for (const { row, col, cellParam } of cellsToProcess) {
      // Skip cells that weren't modified in this specific command (e.g., if only formatting was pasted)
      if (!cellParam || cellParam.v === undefined) continue;

      const fieldDef = this.currentFields[col];
      if (!fieldDef) continue;

      const rawNewValue = cellParam.v;

      const record = this.currentDataMap[row];
      const rowId = record ? record.id || record._id : undefined;
      const originalValue = record ? record[fieldDef.key] : '';
      const isNewRecord = !rowId;
      const effectiveRowId = rowId || `new_${row}`;

      // Advanced UX 1: Read-Only Check
      if (fieldDef.readOnly) {
        // Silently revert the value natively without visual blinking/deleting focus
        let t = 1;
        let v: any = originalValue;
        if (typeof originalValue === 'number') t = 2;
        if (typeof originalValue === 'boolean') {
          t = 3;
          v = originalValue ? 1 : 0;
        }

        this.executeCommandSilently(SetRangeValuesCommand.id, {
          subUnitId: worksheet.getSheetId(),
          unitId: workbook.getUnitId(),
          range: {
            startRow: row,
            endRow: row,
            startColumn: col,
            endColumn: col,
          },
          value: { [row]: { [col]: { v, t } } },
        });

        // Debounce notification warning
        const now = Date.now();
        const lastTime = this.lastNotificationTime.get(fieldDef.key) || 0;
        if (now - lastTime > 2000) {
          this.lastNotificationTime.set(fieldDef.key, now);
          this.notificationService.error(
            $localize`:@@spreadsheet.readOnlyErrorTitle:Read-Only Field`,
            $localize`:@@spreadsheet.readOnlyErrorDesc:${fieldDef.title} field is read-only and cannot be edited.`,
          );
        }
        continue;
      }

      // Advanced UX 2: Parsing & Type Validation
      // We keep invalid inputs so the user can fix them, but we flag them.
      const { parsedValue, isValid } = parseAndConvertCellValue(
        rawNewValue,
        fieldDef.type,
      );

      const normOriginal =
        originalValue === null || originalValue === undefined
          ? ''
          : String(originalValue);
      const normNew =
        parsedValue === null || parsedValue === undefined
          ? ''
          : String(parsedValue);
      const isRevertedToOriginal = normOriginal === normNew;

      // Emit the update event including the validation status and original state matching
      if (effectiveRowId) {
        this.cellUpdateSubject.next({
          rowId: effectiveRowId,
          fieldKey: fieldDef.key,
          oldValue: originalValue,
          newValue: isValid ? parsedValue : rawNewValue, // Ensure we preserve the invalid raw typing if it failed
          rowIndex: row,
          isNewRecord: isNewRecord,
          isValid: isValid,
          isRevertedToOriginal: isRevertedToOriginal,
        });
      }

      // If the value was valid AND needs visual updating (e.g., boolean normalization or Date format matching)
      // We ONLY re-inject valid format conversions if they differ structurally from the raw string.
      if (isValid && parsedValue !== rawNewValue && fieldDef.type !== 'date') {
        if (!silentMatrix) silentMatrix = {};
        if (!silentMatrix[row]) silentMatrix[row] = {};

        let t = 1;
        let displayVal: any = parsedValue;
        if (typeof parsedValue === 'number') t = 2;
        if (typeof parsedValue === 'boolean') {
          t = 3;
          displayVal = parsedValue ? 1 : 0;
        }

        silentMatrix[row][col] = { v: displayVal, t };
        hasSilentUpdates = true;

        minRow = Math.min(minRow, row);
        maxRow = Math.max(maxRow, row);
        minCol = Math.min(minCol, col);
        maxCol = Math.max(maxCol, col);
      }
    }

    // Execute batch silent updates for any type conversions using a dynamically calculated bounding box
    if (hasSilentUpdates) {
      this.executeCommandSilently(SetRangeValuesCommand.id, {
        subUnitId: worksheet.getSheetId(),
        unitId: workbook.getUnitId(),
        range: {
          startRow: minRow,
          endRow: maxRow,
          startColumn: minCol,
          endColumn: maxCol,
        },
        value: silentMatrix,
      });
    }
  }

  /**
   * UX HELPER: Executes a visual animation on a cell when an invalid or read-only edit occurs.
   * Reverts the value, turns the cell red for 2 seconds, blinks 3 times, and resets to normal.
   */
  private triggerCellErrorAnimation(
    row: number,
    col: number,
    originalValue: any,
    fieldName: string,
    isReadOnly: boolean,
  ): void {
    const instanceService = this.univer!.__getInjector().get(
      IUniverInstanceService,
    );
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    const worksheet = workbook.getActiveSheet();

    const subUnitId = worksheet.getSheetId();
    const unitId = workbook.getUnitId();

    // 1. Instantly revert the data value natively
    let t = 1;
    if (typeof originalValue === 'number') t = 2;
    if (typeof originalValue === 'boolean') {
      t = 3;
      originalValue = originalValue ? 1 : 0;
    }

    // CRITICAL FIX: Use silent execution
    this.executeCommandSilently(SetRangeValuesCommand.id, {
      subUnitId,
      unitId,
      range: { startRow: row, endRow: row, startColumn: col, endColumn: col },
      value: {
        [row]: {
          [col]: { v: originalValue !== undefined ? originalValue : '', t },
        },
      },
    });

    // 2. Trigger Standard NG Zorro Notification with Debounce to prevent UI spam
    const now = Date.now();
    const lastTime = this.lastNotificationTime.get(fieldName) || 0;

    if (now - lastTime > 2000) {
      // 2 seconds debounce per field
      this.lastNotificationTime.set(fieldName, now);

      if (isReadOnly) {
        this.notificationService.error(
          $localize`:@@spreadsheet.readOnlyErrorTitle:Read-Only Field`,
          $localize`:@@spreadsheet.readOnlyErrorDesc:${fieldName} field is read-only and cannot be edited.`,
          { nzPlacement: 'bottomRight' },
        );
      } else {
        this.notificationService.error(
          $localize`:@@spreadsheet.invalidInputTitle:Invalid Input`,
          $localize`:@@spreadsheet.invalidInputDesc:Invalid input: ${fieldName} field requires a valid format.`,
          { nzPlacement: 'bottomRight' },
        );
      }
    }

    // 3. Blink Animation Engine
    const redStyle = { bg: { rgb: '#FECACA' } }; // Light Red

    // CRITICAL FIX: Use null instead of '#FFFFFF' to restore transparent background and retain gridlines
    const normalStyle = { bg: null };

    let isRed = false;
    let blinkCount = 0;

    // Force initial red state for 2 seconds
    // CRITICAL FIX: Use silent execution
    this.executeCommandSilently(SetRangeValuesCommand.id, {
      subUnitId,
      unitId,
      range: { startRow: row, endRow: row, startColumn: col, endColumn: col },
      value: { [row]: { [col]: { s: redStyle } } },
    });

    setTimeout(() => {
      isRed = true;
      const blinkInterval = setInterval(() => {
        const style = isRed ? normalStyle : redStyle;
        this.executeCommandSilently(SetRangeValuesCommand.id, {
          subUnitId,
          unitId,
          range: {
            startRow: row,
            endRow: row,
            startColumn: col,
            endColumn: col,
          },
          value: { [row]: { [col]: { s: style } } },
        });

        isRed = !isRed;
        blinkCount++;

        if (blinkCount >= 6) {
          // 3 complete blinks
          clearInterval(blinkInterval);
          // End safely on normal state
          this.executeCommandSilently(SetRangeValuesCommand.id, {
            subUnitId,
            unitId,
            range: {
              startRow: row,
              endRow: row,
              startColumn: col,
              endColumn: col,
            },
            value: { [row]: { [col]: { s: normalStyle } } },
          });
        }
      }, 300);
    }, 2000);
  }

  /**
   * Intercepts paste events on the container to detect tabular data and distribute it correctly.
   */
  private handlePaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const text = clipboardData.getData('text/plain');
    // If it lacks tabs, let Univer handle it as a standard single-cell text paste
    if (!text || text.indexOf('\t') === -1) return;

    event.preventDefault(); // Stop standard dumping

    const tabularData = parseTabularPasteData(text);
    if (tabularData.length === 0) return;

    // Extract the active cell the user wants to start pasting from
    const selectionManager = this.univer!.__getInjector().get(
      SheetsSelectionsService,
    );
    const selections = selectionManager.getCurrentSelections();
    if (!selections || selections.length === 0) return;

    const startRow = selections[0].range.startRow;
    const startCol = selections[0].range.startColumn;

    const instanceService = this.univer!.__getInjector().get(
      IUniverInstanceService,
    );
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    const worksheet = workbook.getActiveSheet();

    const matrix: any = {};

    // Process and validate the incoming data matrix before dispatching
    tabularData.forEach((rowValues, rIdx) => {
      const targetRow = startRow + rIdx;
      // CRITICAL FIX: Removed header protection check. Data now perfectly maps from row 0.
      if (!matrix[targetRow]) matrix[targetRow] = {};

      rowValues.forEach((val, cIdx) => {
        const targetCol = startCol + cIdx;
        const fieldDef = this.currentFields[targetCol];
        if (!fieldDef || fieldDef.readOnly) return;

        const { parsedValue, isValid, displayValue } = parseAndConvertCellValue(
          val,
          fieldDef.type,
          fieldDef.format,
        );
        if (isValid) {
          let t = 1;
          let finalVal =
            displayValue !== undefined ? displayValue : parsedValue;
          if (typeof parsedValue === 'number') t = 2;
          if (typeof parsedValue === 'boolean') {
            t = 3;
            finalVal = parsedValue ? 1 : (0 as any);
          }

          matrix[targetRow][targetCol] = { v: finalVal, t };

          // Emit individual events so the UI tracking knows what happened
          // CRITICAL FIX: Removed `- 1` offset.
          const record = this.currentDataMap[targetRow];
          if (record) {
            this.cellUpdateSubject.next({
              rowId: record.id || record._id,
              fieldKey: fieldDef.key,
              oldValue: record[fieldDef.key],
              newValue: parsedValue,
              rowIndex: targetRow,
              isValid: true,
              isRevertedToOriginal:
                String(record[fieldDef.key]) === String(parsedValue),
            });
          }
        }
      });
    });

    // CRITICAL FIX: Use silent execution
    this.executeCommandSilently(SetRangeValuesCommand.id, {
      subUnitId: worksheet.getSheetId(),
      unitId: workbook.getUnitId(),
      range: {
        startRow,
        endRow: startRow + tabularData.length - 1,
        startColumn: startCol,
        endColumn: startCol + tabularData[0].length - 1,
      },
      value: matrix,
    });
  }

  /**
   * Clean up the Univer instance to prevent memory leaks.
   */
  public destroy(): void {
    if (this.univer) {
      this.univer.dispose();
      this.univer = null;
    }
  }

  /**
   * Visually highlights an entire row in the Univer spreadsheet to indicate state.
   * Utilizes a dual-color system to distinguish between the overall row and the specific changed cells.
   * @param {string | number} rowIndicator - The unique database ID of the record, or row index for new rows.
   * @param {'changed' | 'error' | 'clear' | 'new_ready' | 'new_error'} status - The desired visual state.
   * @param {string[]} changedFields - Optional array of field keys that specifically changed.
   * @param {string[]} errorFields - Optional array of field keys that specifically failed validation or are required but missing.
   */
  public setRowHighlight(
    rowIndicator: string | number,
    status: 'changed' | 'error' | 'clear' | 'new_ready' | 'new_error',
    changedFields: string[] = [],
    errorFields: string[] = [],
  ): void {
    if (!this.univer) return;

    let actualRowIndex = -1;

    if (typeof rowIndicator === 'number') {
      actualRowIndex = rowIndicator;
    } else if (
      typeof rowIndicator === 'string' &&
      rowIndicator.startsWith('new_')
    ) {
      actualRowIndex = parseInt(rowIndicator.replace('new_', ''), 10);
    } else {
      actualRowIndex = this.currentDataMap.findIndex(
        (record) => (record.id || record._id) === rowIndicator,
      );
    }

    if (actualRowIndex === -1) return;

    // Map changed field string keys back to their column index
    const changedColIndices = changedFields
      .map((key) => this.currentFields.findIndex((f) => f.key === key))
      .filter((idx) => idx !== -1);
    const errorColIndices = errorFields
      .map((key) => this.currentFields.findIndex((f) => f.key === key))
      .filter((idx) => idx !== -1);
    const colCount = this.currentFields.length;

    // Determine Base Row Color
    // CRITICAL FIX: Use null for clear state to prevent erasing borders
    let rowBaseColor: string | null = null;
    if (status === 'changed') {
      rowBaseColor = '#FEF9C3'; // Light Yellow 100
    } else if (status === 'error') {
      rowBaseColor = errorColIndices.length > 0 ? '#FEF9C3' : '#FECACA';
    } else if (status === 'new_ready') {
      rowBaseColor = '#DCFCE7'; // Light Green 100
    } else if (status === 'new_error') {
      rowBaseColor = '#DCFCE7';
    }

    // Construct the style matrix for the command
    const matrix: any = { [actualRowIndex]: {} };
    for (let c = 0; c < colCount; c++) {
      const isErrorCell = errorColIndices.includes(c);
      const isChangedCell = changedColIndices.includes(c);

      let cellBg = rowBaseColor;

      if (isErrorCell) {
        cellBg = '#FECACA'; // Red explicitly for missing/invalid cells only
      } else if (isChangedCell && status !== 'clear') {
        cellBg = '#FDE047'; // Dark Yellow 300 for modified valid cells
      }

      matrix[actualRowIndex][c] = {
        s: { bg: cellBg ? { rgb: cellBg } : null },
      };
    }

    // Inject the command to update the cell styles natively
    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;

    const worksheet = workbook.getActiveSheet();

    // Use silent execution
    this.executeCommandSilently(SetRangeValuesCommand.id, {
      subUnitId: worksheet.getSheetId(),
      unitId: workbook.getUnitId(),
      range: {
        startRow: actualRowIndex,
        endRow: actualRowIndex,
        startColumn: 0,
        endColumn: colCount - 1,
      },
      value: matrix,
    });
  }

  /**
   * Reverts specific changes back to their original values natively in the grid and clears their highlights.
   * Prevents the need for a full network reload when users discard changes.
   * @param {Map<string, any>} pendingChanges - The map containing explicit modifications and their row mapping metadata.
   */
  public revertChanges(pendingChanges: Map<string, any>): void {
    if (!this.univer || pendingChanges.size === 0) return;

    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;

    const worksheet = workbook.getActiveSheet();
    const matrix: any = {};

    let minRow = Infinity,
      maxRow = -Infinity,
      minCol = 0,
      maxCol = this.currentFields.length - 1;

    pendingChanges.forEach((changes, rowId) => {
      let actualRowIndex = -1;
      const isNewRecord = changes['_isNewRecord'];

      if (isNewRecord) {
        actualRowIndex = changes['_rowIndex'];
      } else {
        actualRowIndex = this.currentDataMap.findIndex(
          (record) => (record.id || record._id) === rowId,
        );
      }

      if (actualRowIndex === -1) return;

      // 1. Clear highlights immediately using the existing robust style function
      this.setRowHighlight(rowId, 'clear');

      minRow = Math.min(minRow, actualRowIndex);
      maxRow = Math.max(maxRow, actualRowIndex);

      matrix[actualRowIndex] = {};
      const originalRecord = isNewRecord
        ? null
        : this.currentDataMap[actualRowIndex];

      // 2. Map data restoration and completely wipe style overrides to restore gridlines
      for (let c = 0; c < this.currentFields.length; c++) {
        const field = this.currentFields[c];

        if (Object.prototype.hasOwnProperty.call(changes, field.key)) {
          // Identify the original value from the backend
          let originalValue = '';
          if (
            originalRecord &&
            originalRecord[field.key] !== undefined &&
            originalRecord[field.key] !== null
          ) {
            originalValue = originalRecord[field.key];
          }

          let t = 1;
          let v: any = originalValue;

          if (field.type === 'date' && originalValue) {
            const m = moment(originalValue);
            if (m.isValid()) {
              v = m.format(field.format || 'lll');
            }
          } else if (typeof originalValue === 'number') {
            t = 2;
          } else if (typeof originalValue === 'boolean') {
            t = 3;
            v = originalValue ? 1 : 0;
          } else {
            v = String(originalValue);
          }

          // Restore the value, and use `s: null` to completely wipe the style and return the cell's borders
          matrix[actualRowIndex][c] = { v, t, s: null };
        } else {
          // If the cell wasn't changed but its row was highlighted, clear its style to remove highlights
          matrix[actualRowIndex][c] = { s: null };
        }
      }
    });

    if (minRow !== Infinity) {
      this.executeCommandSilently(SetRangeValuesCommand.id, {
        subUnitId: worksheet.getSheetId(),
        unitId: workbook.getUnitId(),
        range: {
          startRow: minRow,
          endRow: maxRow,
          startColumn: minCol,
          endColumn: maxCol,
        },
        value: matrix,
      });
    }
  }

  /**
   * Toggles the visibility of specific columns in the spreadsheet natively.
   * Bypasses the volatile command system to directly manipulate the grid state and force a repaint.
   * @param {number[]} colIndices - Array of column indices to toggle.
   * @param {boolean} isVisible - True to show, false to hide.
   */
  public toggleColumnsVisibility(
    colIndices: number[],
    isVisible: boolean,
  ): void {
    if (!this.univer || colIndices.length === 0) return;

    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;

    const worksheet = workbook.getActiveSheet();

    // 1. Bulletproof Config Override: Directly set the hidden flag in the engine's internal memory
    const config = worksheet.getConfig();
    if (config && config.columnData) {
      colIndices.forEach((col) => {
        if (!config.columnData[col]) {
          config.columnData[col] = {};
        }
        config.columnData[col].hd = isVisible ? 0 : 1; // 0 = visible, 1 = hidden
      });
    }

    // 2. Dispatch the core mutations to safely notify internal plugins without triggering UI commands
    const mutationId = isVisible
      ? 'sheet.mutation.set-col-visible'
      : 'sheet.mutation.set-col-hidden';
    const ranges = colIndices.map((c) => ({
      startColumn: c,
      endColumn: c,
      startRow: 0,
      endRow: worksheet.getRowCount() - 1,
    }));

    this.executeCommandSilently(mutationId, {
      unitId: workbook.getUnitId(),
      subUnitId: worksheet.getSheetId(),
      ranges: ranges,
    });

    // 3. Force the Canvas Renderer to immediately repaint the grid with the new layout
    try {
      const renderManager = this.univer
        .__getInjector()
        .get(IRenderManagerService);
      const currentRender = renderManager.getRenderById(workbook.getUnitId());
      if (currentRender?.mainComponent) {
        currentRender.mainComponent.makeDirty();
      }
    } catch (e) {
      // Safely ignore render pipeline errors
    }
  }

  /**
   * Analyzes the data content and automatically resizes all columns to fit,
   * enforcing robust minimum and maximum limits for UX.
   */
  public autoSizeColumns(): void {
    if (!this.univer || this.currentFields.length === 0) return;

    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;
    const worksheet = workbook.getActiveSheet();

    // Fallback array covering standard and beta Univer command IDs
    const widthCommandIds = [
      'sheet.command.set-col-width',
      'sheet.command.set-worksheet-col-width',
      'sheet.command.set-col-auto-width',
    ];
    let validCommandId: string | null = null;

    this.currentFields.forEach((field: any, colIndex) => {
      // Start with the length of the header title to ensure the header is never cut off
      let maxLen = (field.title || field.key).length;

      // Scan all rows to find the longest string in this column
      this.currentDataMap.forEach((row) => {
        const val = row[field.key];
        const strVal = val !== null && val !== undefined ? String(val) : '';
        if (strVal.length > maxLen) {
          maxLen = strVal.length;
        }
      });

      // Pure approximation: ~8px per character + 24px padding.
      // CRITICAL FIX: Confined stringently between 160px and 500px width
      const calculatedWidth = Math.max(160, Math.min(maxLen * 8 + 24, 500));

      const params = {
        unitId: workbook.getUnitId(),
        subUnitId: worksheet.getSheetId(),
        ranges: [
          {
            startColumn: colIndex,
            endColumn: colIndex,
            startRow: 0,
            endRow: 0,
          },
        ],
        value: calculatedWidth,
      };

      if (validCommandId) {
        this.executeCommandSilently(validCommandId, params);
      } else {
        for (const cmdId of widthCommandIds) {
          const isSuccess = this.executeCommandSilently(cmdId, params);
          if (isSuccess) {
            validCommandId = cmdId; // Cache it so we don't have to loop for the remaining columns!
            break;
          }
        }
      }
    });
  }

  /**
   * Dynamically toggles dark mode without destroying the Univer instance.
   * Leverages the internal ThemeService to paint the UI on the fly.
   * @param {boolean} isDark - The target theme mode.
   */
  public setDarkMode(isDark: boolean): void {
    if (!this.univer) return;

    // ThemeService is not available in @univerjs/core@0.5.5
  }

  public restoreDeletedRows(records: any[]): void {
    if (!this.univer) return;
    const instanceService = this.univer
      .__getInjector()
      .get(IUniverInstanceService);
    const workbook = instanceService.getCurrentUnitForType<any>(
      UniverInstanceType.UNIVER_SHEET,
    );
    if (!workbook) return;
    const worksheet = workbook.getActiveSheet();
    const subUnitId = worksheet.getSheetId();
    const unitId = workbook.getUnitId();

    const silentMatrix: any = {};
    let minRow = Infinity,
      maxRow = -Infinity;

    records.forEach((rec) => {
      if (rec._rowIndex !== undefined) {
        const r = rec._rowIndex;
        silentMatrix[r] = {};
        for (let c = 0; c < this.currentFields.length; c++) {
          silentMatrix[r][c] = { s: null }; // clear background
        }
        minRow = Math.min(minRow, r);
        maxRow = Math.max(maxRow, r);
      }
    });

    if (maxRow >= 0 && minRow !== Infinity) {
      this.executeCommandSilently(SetRangeValuesCommand.id, {
        subUnitId,
        unitId,
        range: {
          startRow: minRow,
          endRow: maxRow,
          startColumn: 0,
          endColumn: this.currentFields.length - 1,
        },
        value: silentMatrix,
      });
    }
  }

  /**
   * Cleans up local maps and disconnects DOM observers when the service is destroyed.
   */
  ngOnDestroy(): void {
    if (this.univer) {
      // CRITICAL FIX: Wrap Univer instance disposal in a try-catch to prevent internal
      // library bugs from breaking the Angular lifecycle during component teardown.
      try {
        this.univer.dispose();
      } catch (e) {
        console.warn(
          '[Univer Debug] Suppressed known internal disposal error:',
          e,
        );
      } finally {
        this.univer = null;
      }
    }

    this.cellUpdateSubject.complete();
    this.selectionSubject.complete();

    if (this.configSaveTimeout) {
      clearTimeout(this.configSaveTimeout);
    }
  }
}
